// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { MessageBubble } from '../../chat/MessageBubble';
import { createTheme } from '../../design-system/theme';
import { pinnedStrings } from './strings';
import type { PinnedMessage } from './types';

export interface PinnedMessageRowProps {
  readonly pinned: PinnedMessage;
  readonly onUnpin?: ((pinned: PinnedMessage) => void) | undefined;
  readonly onJumpToMessage?: ((pinned: PinnedMessage) => void) | undefined;
}

/**
 * Single pinned-message row. Wraps the cycle-14 {@link MessageBubble} with
 * a left pin indicator + the row chrome (jump-to-message + unpin actions).
 */
export const PinnedMessageRow = React.memo<PinnedMessageRowProps>(
  ({ pinned, onUnpin, onJumpToMessage }) => {
    const scheme = useColorScheme();
    const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

    const handleJump = useCallback(() => {
      onJumpToMessage?.(pinned);
    }, [onJumpToMessage, pinned]);

    const handleUnpin = useCallback(() => {
      onUnpin?.(pinned);
    }, [onUnpin, pinned]);

    return (
      <View
        style={[
          styles.container,
          {
            backgroundColor: theme.colors.surface.primary,
            borderBottomColor: theme.colors.border.secondary,
          },
        ]}
      >
        <View
          style={[styles.pinIndicator, { backgroundColor: theme.colors.accent.amber }]}
          accessibilityElementsHidden
        />
        <View style={styles.bubbleColumn}>
          <MessageBubble message={pinned.message} />
          <View style={styles.actionRow}>
            {onJumpToMessage !== undefined && (
              <Pressable
                onPress={handleJump}
                accessibilityRole="button"
                accessibilityLabel={pinnedStrings.jumpToMessageAction}
                style={({ pressed }) => [styles.actionButton, pressed && { opacity: 0.6 }]}
              >
                <Text style={[styles.actionText, { color: theme.colors.accent.indigo }]}>
                  {pinnedStrings.jumpToMessageAction}
                </Text>
              </Pressable>
            )}
            {onUnpin !== undefined && (
              <Pressable
                onPress={handleUnpin}
                accessibilityRole="button"
                accessibilityLabel={pinnedStrings.unpinAction}
                style={({ pressed }) => [styles.actionButton, pressed && { opacity: 0.6 }]}
              >
                <Text style={[styles.actionText, { color: theme.colors.text.secondary }]}>
                  {pinnedStrings.unpinAction}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      </View>
    );
  },
);

PinnedMessageRow.displayName = 'PinnedMessageRow';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  pinIndicator: {
    width: 3,
    borderRadius: 1.5,
    marginRight: 12,
  } satisfies ViewStyle,
  bubbleColumn: {
    flex: 1,
  } satisfies ViewStyle,
  actionRow: {
    flexDirection: 'row',
    marginTop: 6,
    marginLeft: 4,
  } satisfies ViewStyle,
  actionButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginRight: 8,
  } satisfies ViewStyle,
  actionText: {
    fontSize: 13,
    fontWeight: '500',
  } satisfies TextStyle,
});
